import { useCallback, useEffect } from 'react';

import { getRestoredFilters } from './utils';
import { initialFilterTableState } from '../../../__mocks__/table-filters';
import { TableFiltersData, TableFiltersState, TABLE_COLUMN } from '../../filter-table-types';
import { useTableFilters } from '../use-table-filters/use-table-filters';

type UseTableFiltersTypes = ReturnType<typeof useTableFilters>;

export const useLocalStorageTableFilters = (
  LOCAL_STORAGE_KEY: string,
  organisationId: string,
  filterTableState: TableFiltersState,
  filterColumns: TABLE_COLUMN[],
  setInitialFiltersAndSort: UseTableFiltersTypes['setInitialFiltersAndSort']
) => {
  const { sortByColumn, filters, sort } = filterTableState;

  const getLocalStorageFiltersData = useCallback(() => {
    const filtersString = localStorage.getItem(LOCAL_STORAGE_KEY);
    return filtersString ? (JSON.parse(filtersString) as TableFiltersData) : undefined;
  }, [LOCAL_STORAGE_KEY]);

  // Setting initial values according to filters from local storage
  useEffect(() => {
    const allFilterData = getLocalStorageFiltersData();
    const filterData = allFilterData?.[organisationId];

    if (filterData && Object.keys(filterData).length > 0) {
      try {
        const newFilters = {
          ...filterData.filters,
          ...getRestoredFilters(filterData, filterColumns)
        };
        const newSort = filterData.sort.sort;
        const newSortByColumn = filterData.sort.sortByColumn;
        setInitialFiltersAndSort(newFilters, newSort, newSortByColumn);
      } catch (e) {
        // If we failed to read/set the data, reset data back to initial state
        setInitialFiltersAndSort(initialFilterTableState.filters);
      }
    }
  }, [organisationId, setInitialFiltersAndSort, filterColumns, getLocalStorageFiltersData]);

  // When filter is updated, update stored filter values in local storage
  useEffect(() => {
    const storedFiltersString = localStorage.getItem(LOCAL_STORAGE_KEY);
    const storedFiltersData = storedFiltersString ? (JSON.parse(storedFiltersString) as TableFiltersData) : undefined;

    localStorage.setItem(
      LOCAL_STORAGE_KEY,
      JSON.stringify({
        ...(storedFiltersData || {}),
        [organisationId]: { filters, sort: { sortByColumn, sort } }
      })
    );
  }, [filters, organisationId, sortByColumn, LOCAL_STORAGE_KEY, sort]);
};
